import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Check, Repeat, ArrowRight, Puzzle, AlertCircle } from 'lucide-react';
import { api } from '../../lib/api';
import { Platform, PLATFORM_NAMES } from '@crosova/shared';
import { useExtensionDetected, triggerExtensionCrosslist, isExtensionPlatform } from '../../lib/extension';

const PLATFORM_COLOURS: Record<string, string> = {
  ebay: '#e53935', vinted: '#09b864', depop: '#ff2300', etsy: '#f56040', facebook: '#1877f2',
};

export function CrosslistPage() {
  const navigate = useNavigate();
  const extensionDetected = useExtensionDetected();
  const [selectedListings, setSelectedListings] = useState<Set<string>>(new Set());
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<Platform>>(new Set());
  const [extensionStatus, setExtensionStatus] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['crosslist-listings'],
    queryFn: () => api.get('/listings', { params: { status: 'live', limit: 1000 } }).then((r) => r.data),
    staleTime: 0,
  });
  const items = data?.items ?? [];

  const { data: platforms = [] } = useQuery({
    queryKey: ['platforms'],
    queryFn: () => api.get('/platforms').then((r) => r.data),
  });
  const connectedPlatforms = platforms.filter((p: any) => p.connected);

  // Check if any selected platform needs the extension
  const needsExtension = Array.from(selectedPlatforms).some((p) => isExtensionPlatform(p));
  const extensionMissing = needsExtension && !extensionDetected;

  const crosslist = useMutation({
    mutationFn: async () => {
      const extensionPlatforms = Array.from(selectedPlatforms).filter((p) => isExtensionPlatform(p));
      const apiPlatforms = Array.from(selectedPlatforms).filter((p) => !isExtensionPlatform(p));

      for (const id of selectedListings) {
        // Queue API-based platforms normally
        if (apiPlatforms.length > 0) {
          await api.post(`/listings/${id}/crosslist`, { platforms: apiPlatforms });
        }

        // For extension platforms, queue then trigger the extension
        if (extensionPlatforms.length > 0) {
          const { data: result } = await api.post(`/listings/${id}/crosslist`, { platforms: extensionPlatforms });

          // Fetch the listing with photos for the extension
          const { data: listing } = await api.get(`/listings/${id}`);

          // Trigger extension for each platform (one at a time)
          for (const platform of extensionPlatforms) {
            triggerExtensionCrosslist(
              result.jobId,
              {
                title: listing.title,
                description: listing.description,
                price: listing.price,
                condition: listing.condition,
                brand: listing.brand,
                size: listing.size,
                colour: listing.colour,
                tags: listing.tags,
                photos: listing.photos?.map((p: any) => p.url) ?? [],
              },
              platform,
            );
          }
        }
      }
    },
    onSuccess: () => {
      if (needsExtension) {
        setExtensionStatus('Extension is opening the platform — fill in any remaining details and click submit on the platform, then click the green "Mark as listed" button.');
      } else {
        setSelectedListings(new Set());
        setSelectedPlatforms(new Set());
        navigate('/dashboard/activity?tab=jobs');
      }
    },
  });

  function toggleListing(id: string) {
    const next = new Set(selectedListings);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedListings(next);
  }
  function togglePlatform(p: Platform) {
    const next = new Set(selectedPlatforms);
    next.has(p) ? next.delete(p) : next.add(p);
    setSelectedPlatforms(next);
  }

  return (
    <div className="flex flex-col gap-3.5">
      <div>
        <h1 className="text-base font-semibold tracking-tight">Bulk crosslist</h1>
        <p className="text-[11.5px] text-white/25 mt-0.5">Pick items and platforms — Crosova will publish to each one</p>
      </div>

      {/* Extension detection banner */}
      {needsExtension && !extensionDetected && (
        <div className="card p-3 flex items-center gap-3 border border-orange-500/20 bg-orange-500/[0.05]">
          <Puzzle size={18} className="text-orange-400 flex-shrink-0" />
          <div>
            <div className="text-[12.5px] font-medium text-orange-300">Crosova Extension required</div>
            <div className="text-[11px] text-white/40">
              Vinted and Depop need the Crosova Chrome Extension to crosslist. Install it, then refresh this page.
            </div>
          </div>
        </div>
      )}

      {extensionDetected && needsExtension && (
        <div className="card p-3 flex items-center gap-3 border border-emerald-500/20 bg-emerald-500/[0.05]">
          <Check size={18} className="text-emerald-400 flex-shrink-0" />
          <div className="text-[12px] text-emerald-300">Crosova Extension detected — ready to crosslist</div>
        </div>
      )}

      {extensionStatus && (
        <div className="card p-3 flex items-center gap-3 border border-blue-500/20 bg-blue-500/[0.05]">
          <AlertCircle size={18} className="text-blue-400 flex-shrink-0" />
          <div className="text-[12px] text-blue-300">{extensionStatus}</div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
        {/* Listings */}
        <div className="card overflow-hidden">
          <div className="px-4 py-3 border-b border-white/[0.05] flex items-center justify-between">
            <div>
              <div className="text-[13px] font-semibold">Items to crosslist</div>
              <div className="text-[11px] text-white/30">{selectedListings.size} of {items.length} selected</div>
            </div>
            <button
              onClick={() => setSelectedListings(new Set(items.map((i: any) => i.id)))}
              className="text-[11px] text-primary-300 hover:underline"
            >
              Select all
            </button>
          </div>
          <div className="max-h-[380px] overflow-y-auto p-1">
            {items.map((item: any) => {
              const on = selectedListings.has(item.id);
              return (
                <div
                  key={item.id}
                  onClick={() => toggleListing(item.id)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer mb-0.5 ${
                    on ? 'bg-primary-500/[0.08]' : 'hover:bg-white/[0.025]'
                  }`}
                >
                  <div className={`w-3.5 h-3.5 rounded ${on ? 'bg-primary-500 flex items-center justify-center' : 'border border-white/15'}`}>
                    {on && <Check size={10} className="text-white" strokeWidth={3} />}
                  </div>
                  {item.photos?.[0]?.url
                    ? <img src={item.photos[0].url} alt="" className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
                    : <div className="w-8 h-8 rounded-lg bg-white/[0.04] flex-shrink-0" />
                  }
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-medium truncate">{item.title}</div>
                    <div className="text-[10px] text-white/[0.22]">£{(item.price / 100).toFixed(2)} · {item.brand ?? '—'}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Platforms */}
        <div className="card overflow-hidden">
          <div className="px-4 py-3 border-b border-white/[0.05]">
            <div className="text-[13px] font-semibold">Publish to</div>
            <div className="text-[11px] text-white/30">{selectedPlatforms.size} of {connectedPlatforms.length} platforms selected</div>
          </div>
          <div className="p-3 space-y-1.5">
            {connectedPlatforms.map((p: any) => {
              const on = selectedPlatforms.has(p.platform);
              const needsExt = isExtensionPlatform(p.platform);
              return (
                <button
                  key={p.platform}
                  onClick={() => togglePlatform(p.platform)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors ${
                    on ? 'border-secondary-400/30 bg-secondary-400/[0.06]' : 'border-white/[0.07] bg-white/[0.02]'
                  }`}
                >
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs text-white" style={{ background: PLATFORM_COLOURS[p.platform] ?? '#666' }}>
                    {PLATFORM_NAMES[p.platform as Platform].slice(0, 2)}
                  </div>
                  <div className="text-left flex-1">
                    <div className={`text-[13px] font-medium ${on ? 'text-secondary-400' : 'text-white/70'}`}>{PLATFORM_NAMES[p.platform as Platform]}</div>
                    <div className="text-[10px] text-white/30">
                      {needsExt ? (extensionDetected ? 'Via extension' : 'Needs extension') : 'Connected'}
                    </div>
                  </div>
                  <div className={`w-4 h-4 rounded ${on ? 'bg-secondary-400 flex items-center justify-center' : 'border border-white/15'}`}>
                    {on && <Check size={11} className="text-bg-base" strokeWidth={3} />}
                  </div>
                </button>
              );
            })}
            {connectedPlatforms.length === 0 && (
              <div className="text-center py-6 text-xs text-white/30">No platforms connected yet — head to Settings</div>
            )}
          </div>
        </div>
      </div>

      <div className="card p-4 flex items-center justify-between">
        <div className="text-[12px] text-white/50">
          {selectedListings.size > 0 && selectedPlatforms.size > 0
            ? <>Will publish <strong className="text-white">{selectedListings.size}</strong> items to <strong className="text-white">{selectedPlatforms.size}</strong> platforms — <strong className="text-secondary-400">{selectedListings.size * selectedPlatforms.size}</strong> total publish jobs</>
            : 'Pick items and platforms above'}
        </div>
        <button
          onClick={() => crosslist.mutate()}
          disabled={selectedListings.size === 0 || selectedPlatforms.size === 0 || crosslist.isPending || extensionMissing}
          className="btn-primary flex items-center gap-1.5"
        >
          <Repeat size={13} />
          {crosslist.isPending ? 'Queueing...' : 'Crosslist now'}
          <ArrowRight size={13} />
        </button>
      </div>
    </div>
  );
}
